import { z } from 'zod';
import { ChatUiFlightCabinSchema } from '../../shared/schema/flight_cabin_schema.js';
import { ChatUiFlightSegmentSchema } from '../../shared/schema/flight_segment_schema.js';
import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import {
  CheckoutTravelerBlockSchema,
  NamedRefSchema,
} from '../product_checkout/product_checkout_schema.js';

/*
 * O pedido OFFLINE à equipe VOLL — o "concierge" do legado — a partir da bancada
 * determinística de voo, de hotel e de carro. Não há oferta nem preço: o que a pessoa
 * confirma é o PERFIL da busca (herdado, só leitura) mais o que só ela sabe
 * (por que pedir à equipe, restrições, observação). O envio vira um voucher
 * `OFFLINE` no legado, e a cotação volta pelo fluxo de aprovação.
 *
 * Mora ao lado do `product_checkout` e reaproveita as peças dele (viajante,
 * rateio, motivo/justificativa/observação) sem entrar no `CheckoutItem`: um
 * item sem `offerId` e sem `price` violaria o contrato do carrinho e cada regra
 * do checkout online (soma, farol de orçamento, política) teria de aprender um
 * caso especial.
 */

/** Faixa do dia pedida para a partida (o `TakeInfoPeriod` do flight-business). */
export const ConciergeFlightPeriodSchema = z.enum(['morning', 'afternoon', 'night', 'dawn']);
export type ConciergeFlightPeriod = z.infer<typeof ConciergeFlightPeriodSchema>;

const IsoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const AirportRefSchema = z.object({
  iata: z.string().min(3).max(3),
  /** Cidade para exibição; ausente ⇒ o IATA fala sozinho. */
  city: z.string().optional(),
});

/** Um trecho pedido à equipe — o que a busca tinha (rota + dia) e o que a
 *  pessoa acrescenta na tela (o período). */
export const ConciergeFlightSegmentSchema = z.object({
  date: IsoDay,
  origin: AirportRefSchema,
  destination: AirportRefSchema,
  period: ConciergeFlightPeriodSchema.optional(),
});
export type ConciergeFlightSegment = z.infer<typeof ConciergeFlightSegmentSchema>;

/** A IDA que a pessoa já escolheu online quando a VOLTA não teve resultado: vai
 *  como referência no pedido e NÃO é emitida — a equipe cota a volta e a ida é
 *  aprovada junto com a cotação (wireframe A2). */
export const ConciergeHeldFlightSchema = z.object({
  segment: ChatUiFlightSegmentSchema,
  price: ChatUiMoneySchema.optional(),
  fareClass: z.string().optional(),
});
export type ConciergeHeldFlight = z.infer<typeof ConciergeHeldFlightSchema>;

const RequestBaseSchema = z.object({
  /** Os chips do perfil herdado da busca, JÁ formatados pela página que os
   *  mostrou (os mesmos do banner) — só leitura no checkout. */
  profile: z.array(z.string().min(1)),
});

export const ConciergeFlightRequestSchema = RequestBaseSchema.extend({
  productType: z.literal('flight'),
  segments: z.array(ConciergeFlightSegmentSchema).min(1),
  cabin: ChatUiFlightCabinSchema.optional(),
  heldOutbound: ConciergeHeldFlightSchema.optional(),
});
export type ConciergeFlightRequest = z.infer<typeof ConciergeFlightRequestSchema>;

export const ConciergeHotelRequestSchema = RequestBaseSchema.extend({
  productType: z.literal('hotel'),
  /** O lugar da busca como a pessoa o leu ("Perto do Escritório São Paulo"). */
  place: z.string().min(1),
  /** Cidade para o título do painel; ausente ⇒ o lugar faz de título. */
  city: z.string().optional(),
  checkIn: IsoDay,
  checkOut: IsoDay,
  nights: z.number().int().min(1),
  rooms: z.number().int().min(1),
  guests: z.number().int().min(1),
  /** Teto por noite da política/filtro, quando houver — viaja como perfil. */
  maxNightly: ChatUiMoneySchema.optional(),
});
export type ConciergeHotelRequest = z.infer<typeof ConciergeHotelRequestSchema>;

/** Uma rodoviária da busca: o slug do bus-api e o nome que a pessoa escolheu. */
const BusStopRefSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
});

/** O pedido de ÔNIBUS: a busca como a pessoa a fez. O legado (`voll_bus`) não
 *  manda rota nem datas ao bus-api — ele as tira do `searchId` da ida, que existe
 *  mesmo quando a busca veio vazia; rota e datas ficam para a tela e a viagem. */
export const ConciergeBusRequestSchema = RequestBaseSchema.extend({
  productType: z.literal('bus'),
  searchId: z.string().min(1),
  from: BusStopRefSchema,
  to: BusStopRefSchema,
  outwardDate: IsoDay,
  /** Ausente ⇔ só ida. */
  returnDate: IsoDay.optional(),
});
export type ConciergeBusRequest = z.infer<typeof ConciergeBusRequestSchema>;

/** Um ponto da locação como a busca o conhece: o ref opaco do places
 *  (`airport:GRU` | `google:<placeId>`) — o BFF o re-hidrata para o pedido — e
 *  o rótulo que a pessoa leu. */
const RentalPlaceRefSchema = z.object({
  ref: z.string().min(1),
  label: z.string().trim().min(1),
});

export const ConciergeRentalRequestSchema = RequestBaseSchema.extend({
  productType: z.literal('rental'),
  pickup: RentalPlaceRefSchema,
  /** Ausente ⇒ devolução no MESMO lugar da retirada. */
  dropoff: RentalPlaceRefSchema.optional(),
  /** Dia+hora local, sem fuso (`YYYY-MM-DDTHH:mm`) — o mesmo da busca. */
  pickupAt: z.iso.datetime({ local: true }),
  dropoffAt: z.iso.datetime({ local: true }),
  days: z.number().int().min(1),
  /** A categoria do filtro da busca, quando era UMA só: entra marcada. Código
   *  da faceta (`SUV`) ou do catálogo (`suv`) — casa sem caixa. */
  categoryHint: z.string().min(1).optional(),
});
export type ConciergeRentalRequest = z.infer<typeof ConciergeRentalRequestSchema>;

export const ConciergeRequestSchema = z.discriminatedUnion('productType', [
  ConciergeFlightRequestSchema,
  ConciergeHotelRequestSchema,
  ConciergeBusRequestSchema,
  ConciergeRentalRequestSchema,
]);
export type ConciergeRequest = z.infer<typeof ConciergeRequestSchema>;
export type ConciergeProductType = ConciergeRequest['productType'];

/** Os produtos com catálogo "por que pedir à equipe" no concierge-api. O carro
 *  não tem: o `rentalCarBooking` não carrega motivo do pedido. */
export type ConciergeReasonProductType = Exclude<ConciergeProductType, 'rental'>;

/** Um motivo do catálogo "por que pedir à equipe" (`conciergeFlowReason`). */
export const ConciergeReasonOptionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  isDefault: z.boolean().optional(),
});
export type ConciergeReasonOption = z.infer<typeof ConciergeReasonOptionSchema>;

/** Uma opção dos catálogos do pedido de carro (categoria / locadora). */
export const ConciergeRentalOptionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** A logo da locadora, quando o rental-car-api a tem; a categoria não tem. */
  imageUrl: z.string().min(1).optional(),
});
export type ConciergeRentalOption = z.infer<typeof ConciergeRentalOptionSchema>;

/** Os catálogos do pedido de carro (rental-car-api): no vazio da busca não há
 *  facetas, então o cartão não pode depender delas. */
export const ConciergeRentalOptionsSchema = z.object({
  categories: z.array(ConciergeRentalOptionSchema),
  companies: z.array(ConciergeRentalOptionSchema),
});
export type ConciergeRentalOptions = z.infer<typeof ConciergeRentalOptionsSchema>;

/** Tipo de cama do pedido de hotel (`HOTEL_ENUM_BED_TYPE` do hotels-api). */
export const ConciergeBedTypeSchema = z.enum(['UNDEFINED', 'SINGLE', 'DOUBLE']);
export type ConciergeBedType = z.infer<typeof ConciergeBedTypeSchema>;

/** Onde no ônibus (`BusSeatPosition` do bus-api). */
export const ConciergeBusSeatPositionSchema = z.enum(['FRONT', 'MIDDLE', 'BACK']);
export type ConciergeBusSeatPosition = z.infer<typeof ConciergeBusSeatPositionSchema>;

/** Onde na fileira (`BusSeatRowPosition` do bus-api). */
export const ConciergeBusSeatRowPositionSchema = z.enum(['WINDOW_SEAT', 'AISLE_SEAT']);
export type ConciergeBusSeatRowPosition = z.infer<typeof ConciergeBusSeatRowPositionSchema>;

/** Classe pedida (`SeatType` do bus-api, sem o `unknown`). */
export const ConciergeBusSeatClassSchema = z.enum([
  'ALL',
  'CONVENTIONAL',
  'EXECUTIVE',
  'HALF_BED',
  'BED',
  'BED_FULL',
]);
export type ConciergeBusSeatClass = z.infer<typeof ConciergeBusSeatClassSchema>;

/** Tamanho da observação livre — o MESMO teto do `observation` do checkout. */
export const CONCIERGE_DETAILS_MAX = 300;

/**
 * O payload que o checkout no modo `request` entrega ao host no submit. A metade
 * corporativa (`travelers`/`motive`/`justification`/`observation`) é a do
 * `CheckoutSubmit` multi-viajante — o BFF reaproveita o mesmo mapper de
 * viajante/rateio; a outra metade é o pedido em si.
 */
export const ConciergeSubmitSchema = z.object({
  productType: z.enum(['flight', 'hotel', 'bus', 'rental']),
  travelers: z.array(CheckoutTravelerBlockSchema).min(1),
  policyTravelerId: z.string().min(1).optional(),
  motive: NamedRefSchema,
  justification: NamedRefSchema.optional(),
  observation: z.string().max(300).optional(),
  /** Ausente só quando o catálogo de motivos não respondeu — o pedido segue
   *  sem motivo em vez de ficar preso num catálogo fora do ar. */
  reason: NamedRefSchema.optional(),
  details: z.string().max(CONCIERGE_DETAILS_MAX).optional(),
  flight: z
    .object({
      onlyDirect: z.boolean(),
      onlyWithLuggage: z.boolean(),
      segments: z.array(ConciergeFlightSegmentSchema).min(1),
    })
    .optional(),
  hotel: z
    .object({
      preferredHotel: z.string().min(1).optional(),
      bedType: ConciergeBedTypeSchema.optional(),
    })
    .optional(),
  /** As preferências do ônibus — todas obrigatórias no legado; o período da
   *  volta só na ida-e-volta. A descrição obrigatória é o `details`. */
  bus: z
    .object({
      outwardPeriod: ConciergeFlightPeriodSchema,
      returnPeriod: ConciergeFlightPeriodSchema.optional(),
      seatPosition: ConciergeBusSeatPositionSchema,
      seatRowPosition: ConciergeBusSeatRowPositionSchema,
      seatClass: ConciergeBusSeatClassSchema,
    })
    .optional(),
  /** Categoria obrigatória no rental-car-api (a política sai dela); locadora
   *  opcional. No carro `details` também é obrigatório: é o `concierge.obs`. */
  rental: z
    .object({
      category: NamedRefSchema,
      company: NamedRefSchema.optional(),
    })
    .optional(),
});
export type ConciergeSubmit = z.infer<typeof ConciergeSubmitSchema>;
